import { useState } from "react"
import {Layers, Plus, Trash2, ChevronDown, ChevronUp, Clock, Thermometer, Info} from 'lucide-react'
import { useCalculator, type StageType, STAGE_TIMING_GUIDANCE } from "@/context/CalculatorContext"

const STAGE_TYPE_OPTIONS: { label: string; value: StageType; hint: string }[] = [
  { label: "Preferment", value: "preferment", hint: "Poolish, biga, or sponge built ahead of the final mix" },
  { label: "Autolyse", value: "autolyse", hint: "Flour + water rested before mixing, no leavening" },
  { label: "Soaker", value: "soaker", hint: "Grains, seeds, or inclusions hydrated in advance" },
  { label: "Custom Stage", value: "custom", hint: "Define your own allocation" },
]

const TYPE_BADGE_STYLES: Record<StageType, string> = {
  preferment: "bg-orange-50 text-orange-700 border-orange-200",
  autolyse: "bg-sky-50 text-sky-700 border-sky-200",
  soaker: "bg-amber-50 text-amber-700 border-amber-200",
  custom: "bg-gray-100 text-gray-600 border-gray-200",
}

function formatNum(n: number, decimals = 0) {
  if (!isFinite(n) || isNaN(n)) return "0"
  return n.toFixed(decimals)
}

export default function DoughStagesCard() {
  const {
    ingredients,
    stages,
    addStage,
    removeStage,
    updateStageName,
    updateStageRestTime,
    updateStageAllocation,
    getStageBreakdown,
    finalDoughRemainder,
    finalDoughWeight,
    bulkFermentationTime,
    setBulkFermentationTime,
    finalProofTime,
    setFinalProofTime,
  } = useCalculator()

  const [showDropdown, setShowDropdown] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const handleAdd = (type: StageType) => {
    addStage(type)
    setShowDropdown(false)
  }

  const applyTimingPreset = (stageId: string, type: StageType) => {
    updateStageRestTime(stageId, STAGE_TIMING_GUIDANCE[type].durationRange)
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 card-hover">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <Layers size={17} className="text-sky-600" strokeWidth={2.2} />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-gray-900 leading-tight">
            Dough Stages
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Plan preferments, autolyse, and soakers from your master formula
          </p>
        </div>
      </div>

      {/* Stage list */}
      {stages.length > 0 && (
        <div className="space-y-2 mb-4">
          {stages.map((stage) => {
            const breakdown = getStageBreakdown(stage.id)
            const isExpanded = expandedId === stage.id
            const guidance = STAGE_TIMING_GUIDANCE[stage.type]
            return (
              <div
                key={stage.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Stage header row */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : stage.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100/70 transition-colors text-left"
                >
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${TYPE_BADGE_STYLES[stage.type]}`}
                  >
                    {STAGE_TYPE_OPTIONS.find((o) => o.value === stage.type)?.label || "Stage"}
                  </span>
                  <span className="text-sm font-medium text-gray-800 truncate flex-1">
                    {stage.name}
                  </span>
                  <span className="text-xs font-bold text-gray-600 tabular-nums bg-white border border-gray-200 rounded-md px-2 py-0.5 flex-shrink-0">
                    {formatNum(breakdown.weight)}g
                  </span>
                  {isExpanded ? (
                    <ChevronUp size={15} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-3 animate-fade-up">
                    {/* Name + rest time */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          Stage Name
                        </label>
                        <input
                          type="text"
                          value={stage.name}
                          onChange={(e) => updateStageName(stage.id, e.target.value)}
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                          <Clock size={10} className="inline mr-1 -mt-0.5" />
                          Rest Time
                        </label>
                        <input
                          type="text"
                          value={stage.restTime || ""}
                          onChange={(e) => updateStageRestTime(stage.id, e.target.value)}
                          placeholder="e.g. 12 hours"
                          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Timing & scheduling guidance */}
                    <div className="bg-sky-50/70 border border-sky-100 rounded-lg px-3 py-2.5 mb-3">
                      <div className="flex items-start gap-2">
                        <Thermometer size={13} className="text-sky-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1">
                            <p className="text-xs font-semibold text-sky-800">
                              {guidance.tempRange}
                            </p>
                            <span className="text-sky-300">&middot;</span>
                            <p className="text-xs font-semibold text-sky-800">
                              {guidance.durationRange}
                            </p>
                            <button
                              onClick={() => applyTimingPreset(stage.id, stage.type)}
                              className="text-xs font-semibold text-sky-600 hover:text-sky-700 underline underline-offset-2 ml-auto"
                            >
                              Use this timing
                            </button>
                          </div>
                          <p className="text-xs text-sky-700 leading-relaxed">{guidance.tip}</p>
                        </div>
                      </div>
                    </div>

                    {/* Allocation table */}
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Ingredient Allocation
                    </p>
                    <div className="space-y-1.5 mb-3">
                      {ingredients.map((ing) => {
                        const alloc = stage.allocations.find((a) => a.ingredientId === ing.id)
                        const pct = alloc?.percentage ?? 0
                        const weight = (ing.weight * pct) / 100
                        return (
                          <div key={ing.id} className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 w-28 truncate flex-shrink-0">
                              {ing.name}
                            </span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              step="1"
                              value={pct}
                              onChange={(e) =>
                                updateStageAllocation(stage.id, ing.id, Number(e.target.value))
                              }
                              className="flex-1"
                              style={{
                                background: `linear-gradient(to right, #4F46E5 0%, #4F46E5 ${pct}%, #E5E7EB ${pct}%, #E5E7EB 100%)`,
                              }}
                            />
                            <span className="text-xs font-semibold text-gray-700 tabular-nums w-9 text-right flex-shrink-0">
                              {formatNum(pct)}%
                            </span>
                            <span className="text-xs text-gray-400 tabular-nums w-14 text-right flex-shrink-0">
                              {formatNum(weight)}g
                            </span>
                          </div>
                        )
                      })}
                    </div>

                    {/* Breakdown summary */}
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">Stage Weight</p>
                        <p className="text-sm font-bold text-gray-800 tabular-nums">
                          {formatNum(breakdown.weight)}g
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-2.5 py-2 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">Flour / Water</p>
                        <p className="text-sm font-bold text-gray-800 tabular-nums">
                          {formatNum(breakdown.flourWeight)}/{formatNum(breakdown.waterWeight)}g
                        </p>
                      </div>
                      <div className="bg-indigo-50 rounded-lg px-2.5 py-2 text-center border border-indigo-100">
                        <p className="text-xs text-indigo-400 mb-0.5">Hydration</p>
                        <p className="text-sm font-bold text-indigo-700 tabular-nums">
                          {formatNum(breakdown.hydration, 1)}%
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => removeStage(stage.id)}
                      className="flex items-center gap-1.5 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <Trash2 size={12} />
                      Remove Stage
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Final dough remainder summary */}
      {stages.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
              Final Mix (After Stages)
            </p>
            <span className="text-sm font-bold text-emerald-700 tabular-nums">
              {formatNum(finalDoughWeight)}g
            </span>
          </div>
          <div className="space-y-1">
            {finalDoughRemainder
              .filter((item) => item.allocatedWeight > 0.01)
              .map((item) => (
                <div key={item.id} className="flex items-center justify-between text-xs">
                  <span className="text-emerald-600">{item.name}</span>
                  <span className="font-medium text-emerald-700 tabular-nums">
                    {formatNum(item.remainingWeight)}g remaining
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add stage */}
      <div className="relative">
        <button
          onClick={() => setShowDropdown((v) => !v)}
          className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700 bg-sky-50 hover:bg-sky-100 px-3 py-2.5 rounded-lg transition-colors"
        >
          <Plus size={15} strokeWidth={2.5} />
          Add Dough Stage
        </button>

        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-10">
            {STAGE_TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleAdd(opt.value)}
                className="w-full text-left px-4 py-2.5 hover:bg-sky-50 transition-colors"
              >
                <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{opt.hint}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {stages.length === 0 && (
        <p className="text-xs text-gray-400 mt-3 text-center">
          Split your formula into a preferment, autolyse, or soaker without changing your master weights.
        </p>
      )}

      {/* Baking Day Timeline — bulk fermentation & final proof scheduling */}
      <div className="mt-5 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={14} className="text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Baking Day Timeline</h3>
          <div className="group relative">
            <Info size={12} className="text-gray-300 cursor-help" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-56 bg-gray-900 text-white text-xs rounded-lg px-2.5 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              Covers the steps after your stages are mixed in: bulk fermentation of the final dough and final proof before baking.
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Bulk Fermentation
            </label>
            <input
              type="text"
              value={bulkFermentationTime}
              onChange={(e) => setBulkFermentationTime(e.target.value)}
              placeholder="e.g. 4-6 hours at room temp"
              className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">
              Dough should grow noticeably in volume and feel airy/jiggly, not just taller.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Final Proof (Shaped Dough)
            </label>
            <input
              type="text"
              value={finalProofTime}
              onChange={(e) => setFinalProofTime(e.target.value)}
              placeholder="e.g. 45-90 min at room temp"
              className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">
              Use the poke test: dough should spring back slowly and leave a slight indent.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
